import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronUp } from "lucide-react";
import { CALENDLY_URL, EMAIL } from "../constants/site";
import "./Navbar.css";

const NAV_LINKS = [
    { label: "Case Studies", id: "work" },
    { label: "Services", id: "services" },
    { label: "Process", id: "process" },
    { label: "FAQs", id: "faqs" },
    { label: "Book a Call", id: "book" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const goTo = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (!el) return;
        if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <>
            <header
                className={`navbar${scrolled ? " navbar-scrolled" : ""}`}
                data-testid="navbar"
            >
                <div className="navbar-inner">
                    <a
                        href="#top"
                        className="navbar-logo"
                        onClick={(e) => {
                            e.preventDefault();
                            goTo("top");
                        }}
                        data-testid="navbar-logo"
                    >
                        <img
                            src="/brand/growco-wordmark.png"
                            alt="GrowCo"
                            className="navbar-logo-img"
                        />
                    </a>
                    <div className="navbar-actions">
                        <a
                            className="navbar-book-link"
                            href={CALENDLY_URL}
                            target="_blank"
                            rel="noopener"
                            data-testid="navbar-book-call-link"
                        >
                            Book a call
                        </a>
                        <button
                            type="button"
                            className="navbar-menu-button"
                            onClick={() => setOpen((v) => !v)}
                            aria-expanded={open}
                            aria-label={open ? "Close menu" : "Open menu"}
                            data-testid="navbar-menu-button"
                        >
                            Menu
                            <ChevronUp size={16} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </header>

            <div
                className={`nav-drawer${open ? " nav-drawer-open" : ""}`}
                aria-hidden={!open}
                data-testid="nav-drawer"
            >
                <motion.nav
                    className="nav-drawer-links"
                    aria-label="Site"
                    initial={false}
                    animate={open ? "open" : "closed"}
                    variants={{
                        open: {
                            transition: {
                                staggerChildren: 0.06,
                                delayChildren: 0.1,
                            },
                        },
                        closed: {},
                    }}
                >
                    {NAV_LINKS.map((link) => (
                        <motion.button
                            key={link.id}
                            type="button"
                            className="nav-drawer-link"
                            onClick={() => goTo(link.id)}
                            tabIndex={open ? 0 : -1}
                            variants={{
                                open: {
                                    opacity: 1,
                                    y: 0,
                                    transition: {
                                        duration: 0.45,
                                        ease: [0.16, 1, 0.3, 1],
                                    },
                                },
                                closed: { opacity: 0, y: 24 },
                            }}
                            data-testid={`drawer-link-${link.id}`}
                        >
                            {link.label}
                        </motion.button>
                    ))}
                </motion.nav>
                <div className="nav-drawer-footer">
                    <a
                        href={`mailto:${EMAIL}`}
                        className="nav-drawer-email"
                        data-testid="drawer-email"
                    >
                        {EMAIL}
                    </a>
                    <span className="nav-drawer-copy">
                        © 2024 Tondku LLP. All rights reserved.
                    </span>
                </div>
            </div>
        </>
    );
};
